import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { scrollToSection, consumePendingSection } from '@/lib/sectionNav';

// Sección destino que viaja por el estado de navegación (no por el hash). Es el
// mecanismo que usa SectionLink (y que ya usaba "Inicio", el único botón que
// siempre funcionó).
interface NavState {
  scrollTo?: string;
}

// Único controlador del scroll al cambiar de ruta en TODA la app.
//
// Orden de prioridad al llegar a una ruta nueva:
// 1) `state.scrollTo` → baja a esa sección del inicio (mecanismo fiable, sin hash).
// 2) `#ancla` en la URL → baja a esa sección (enlaces nativos internos del inicio).
// 3) Intención guardada en sessionStorage → sobrevive a recargas del navegador,
//    donde el `state` de React Router se pierde. Es la red de seguridad que hacía
//    que, al recargar, TODOS los botones cayeran arriba del inicio.
// 4) Sin destino → vuelve al tope de la página.
//
// Se apoya en `location.key` para reaccionar a CADA navegación (incluidas las que
// van a la misma ruta con distinto estado). Al usar el botón "atrás" del
// navegador respeta la posición previa (salvo que haya una intención pendiente).
export default function ScrollManager() {
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    const fromState = (location.state as NavState | null)?.scrollTo;
    const hashTarget = location.hash ? decodeURIComponent(location.hash.slice(1)) : '';
    // Leemos SIEMPRE la intención guardada y la limpiamos, para no dejar valores
    // viejos que disparen saltos en navegaciones futuras.
    const pending = consumePendingSection();
    const target = fromState || hashTarget || pending;

    if (target) {
      scrollToSection(target);
      return undefined;
    }

    // Navegación hacia atrás sin destino explícito: dejamos que el navegador
    // restaure la posición previa del historial.
    if (navigationType === 'POP') return undefined;

    window.scrollTo({ top: 0, behavior: 'auto' });
    return undefined;
  }, [location.key, location.pathname, location.hash, location.state, navigationType]);

  return null;
}